import AppFormContainer from '@nxsys/components/AppFormContainer';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { Form, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import StepCard from './StepCard';

interface AddCaptureIDDocsProps {
  stepNumber: number;
  captureIdDocs?: string | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddCaptureIDDocsValues = {
  captureIdDocsValue?: UploadFile[];
};

const normFile = (e: any): UploadFile[] => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList ?? [];
};

const AddCaptureIDDocs: React.FC<AddCaptureIDDocsProps> = ({
  stepNumber,
  captureIdDocs,
  completed = false,
  onSubmitStep,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm<AddCaptureIDDocsValues>();
  const { messages } = useIntl();
  // Covers the S3 upload below, which happens before onSubmitStep and so is not
  // covered by the onboarding modal's loader.
  const [saving, setSaving] = useState(false);

  const onFinish = async (values: AddCaptureIDDocsValues) => {
    let params = {};
    let url = null;
    // An already-saved document is seeded into the fileList as `{ name }` with
    // no `originFileObj`, so only upload when the user actually picked a new
    // file — otherwise keep the existing captureIdDocs.
    const newFile = values.captureIdDocsValue?.[0]?.originFileObj as
      | File
      | undefined;
    if (newFile) {
      try {
        setSaving(true);
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: newFile }),
        ).unwrap();
        url = res?.url;
      } finally {
        setSaving(false);
      }
    }
    params = {
      captureIdDocs: url ?? captureIdDocs,
    };
    onSubmitStep(params);
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.addCaptureIDDocs'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
          loading: saving,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          captureIdDocsValue: captureIdDocs
            ? [
                {
                  name: captureIdDocs,
                },
              ]
            : [],
        }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
          <Form.Item
            label={messages["prospect.onboarding.uploadIDDocs"] as string}
            name="captureIdDocsValue"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[
              {
                required: true,
                message: messages[
                  'prospect.onboarding.captureIdDocs.required'
                ] as string,
              },
            ]}
          >
            <Upload.Dragger
              name="files"
              beforeUpload={() => false}
              multiple={false}
              maxCount={1}
              accept=".svg,.png,.jpg,.jpeg,.gif"
            >
              <p className="ant-upload-text">
                {messages['prospect.onboarding.upload.text'] as string}
              </p>
              <p className="ant-upload-hint">
                {messages['prospect.onboarding.upload.hint'] as string}
              </p>
            </Upload.Dragger>
          </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddCaptureIDDocs;
